/**
 * UranIA PA — API client tipi documento (admin + lettura funzionario).
 *
 * Wrapper su:
 *   - ``GET /api/v1/tipi-documento``        (visibile a qualsiasi autenticato)
 *   - ``GET /api/v1/admin/tipi-documento``  (admin: tutti)
 *   - ``POST /api/v1/admin/tipi-documento`` (create)
 *   - ``PUT /api/v1/admin/tipi-documento/{id}`` (update)
 *   - ``DELETE /api/v1/admin/tipi-documento/{id}`` (soft delete)
 */

import { useAuthStore } from '../auth/auth-store.ts';
import { loadSpaEnv } from '../env.ts';

export type TipoDocumentoScope = 'core' | 'tenant';

export type TipoDocumentoIcona =
  | 'base_giuridica'
  | 'avviso'
  | 'decreto_graduatoria'
  | 'decreto_finanziamento'
  | 'checklist_ricevibilita'
  | 'checklist_ammissibilita'
  | 'documento_generico';

export interface TipoDocumentoSezioneDto {
  readonly heading: string;
  readonly body: string;
}

export interface TipoDocumentoDto {
  readonly tipo_id: string;
  readonly tenant_id: string | null;
  readonly label: string;
  readonly titolo_docx: string;
  readonly icona: TipoDocumentoIcona;
  readonly descrizione: string;
  readonly sezioni: readonly TipoDocumentoSezioneDto[];
  readonly is_active: boolean;
  readonly is_core: boolean;
  readonly created_at: string;
  readonly created_by: string | null;
  readonly updated_at: string | null;
  readonly updated_by: string | null;
  readonly version: number;
}

export interface ListTipiDocumentoDto {
  readonly items: readonly TipoDocumentoDto[];
  readonly total: number;
}

export interface UpsertTipoDocumentoInput {
  readonly tipo_id: string;
  readonly scope: TipoDocumentoScope;
  readonly label: string;
  readonly titolo_docx: string;
  readonly icona: TipoDocumentoIcona;
  readonly descrizione: string;
  readonly sezioni: readonly TipoDocumentoSezioneDto[];
}

export class TipiDocumentoApiError extends Error {
  public readonly status: number;
  public readonly detail: string | null;
  constructor(status: number, title: string, detail: string | null) {
    super(title);
    this.name = 'TipiDocumentoApiError';
    this.status = status;
    this.detail = detail;
  }
}

function authHeader(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  if (token === null || token.length === 0) return {};
  return { Authorization: `Bearer ${token}` };
}

function buildUrl(path: string): string {
  const env = loadSpaEnv();
  const base = env.apiBaseUrl.replace(/\/+$/u, '');
  return `${base}${path}`;
}

async function parseProblem(response: Response): Promise<TipiDocumentoApiError> {
  let title = response.statusText || 'http_error';
  let detail: string | null = null;
  try {
    const body = (await response.json()) as { title?: unknown; detail?: unknown };
    if (typeof body.title === 'string' && body.title.length > 0) title = body.title;
    if (typeof body.detail === 'string') detail = body.detail;
  } catch {
    // no problem+json
  }
  return new TipiDocumentoApiError(response.status, title, detail);
}

async function requestJson<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...authHeader(),
    },
    credentials: 'include',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };
  const response = await fetch(buildUrl(path), init);
  if (!response.ok) {
    throw await parseProblem(response);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export async function listTipiVisible(): Promise<ListTipiDocumentoDto> {
  return requestJson<ListTipiDocumentoDto>('GET', '/api/v1/tipi-documento');
}

export async function listTipiAdmin(): Promise<ListTipiDocumentoDto> {
  return requestJson<ListTipiDocumentoDto>('GET', '/api/v1/admin/tipi-documento');
}

export async function createTipoDocumento(
  input: UpsertTipoDocumentoInput,
): Promise<TipoDocumentoDto> {
  return requestJson<TipoDocumentoDto>('POST', '/api/v1/admin/tipi-documento', input);
}

export async function updateTipoDocumento(
  tipoId: string,
  input: UpsertTipoDocumentoInput,
): Promise<TipoDocumentoDto> {
  return requestJson<TipoDocumentoDto>(
    'PUT',
    `/api/v1/admin/tipi-documento/${encodeURIComponent(tipoId)}`,
    input,
  );
}

export async function deleteTipoDocumento(tipoId: string): Promise<void> {
  await requestJson<void>(
    'DELETE',
    `/api/v1/admin/tipi-documento/${encodeURIComponent(tipoId)}`,
  );
}
